import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroExclamationTriangle } from '@ng-icons/heroicons/outline';
import { UiPanelCard, UiProgressBar } from '../../../shared/ui';
import { AgentBuilderStore } from './agent-builder.store';
import { AgentMissionQuillEditorComponent } from './agent-mission-quill-editor.component';

@Component({
  selector: 'app-agent-mission-editor-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, UiPanelCard, UiProgressBar, AgentMissionQuillEditorComponent],
  providers: [
    provideIcons({
      heroExclamationTriangle,
    }),
  ],
  template: `
    <section class="mission-editor">
      @if (
        store.streamErrors().length > 0 ||
        store.streamWarnings().length > 0 ||
        store.missionDiagnostics().length > 0
      ) {
        <div class="mission-editor__messages" aria-live="polite">
          @for (message of store.streamErrors(); track message) {
            <p appUiPanelCard class="message message--error">
              <ng-icon name="heroExclamationTriangle" size="15" aria-hidden="true" />
              {{ message }}
            </p>
          }
          @for (message of store.streamWarnings(); track message) {
            <p appUiPanelCard class="message">
              <ng-icon name="heroExclamationTriangle" size="15" aria-hidden="true" />
              {{ message }}
            </p>
          }
          @for (
            diagnostic of store.missionDiagnostics();
            track diagnostic.code + diagnostic.message + $index
          ) {
            <p appUiPanelCard class="message" [class.message--error]="diagnostic.severity === 'error'">
              <ng-icon name="heroExclamationTriangle" size="15" aria-hidden="true" />
              {{ diagnostic.message }}
            </p>
          }
        </div>
      }

      @if (isGenerating()) {
        <div class="progress">
          <app-ui-progress-bar [heightRem]="0.35" />
          <span>{{
            store.missionRunStatus() === 'queued' ? 'Aguardando worker...' : 'Gerando missao...'
          }}</span>
        </div>
      }

      <div class="mission-workbench">
        <div class="mission-document" aria-label="Documento da missão">
          <div appUiPanelCard class="mission-document__paper surface-noise">
            <app-agent-mission-quill-editor
              [document]="store.editableMissionDocument()"
              [mentionOptions]="store.missionMentionOptions()"
              [generating]="isGenerating()"
              [hasGeneratedMission]="!!store.missionDocument()"
              [generateDisabled]="isGenerating() || store.saving()"
              (documentChange)="store.replaceMissionFromEditor($event)"
              (generateRequested)="store.startMissionGeneration()"
            />
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .mission-editor {
        display: grid;
        min-width: 0;
        gap: 0.7rem;
        padding: 0;
      }

      .mission-editor__messages {
        display: grid;
        gap: 0.4rem;
      }

      .message {
        display: flex;
        align-items: flex-start;
        min-width: 0;
        gap: 0.45rem;
        margin: 0;
        color: var(--color-muted);
        padding: 0.6rem 0.7rem;
        font-size: 0.82rem;
        font-weight: 650;
        line-height: 1.45;
      }

      .message--error {
        border-color: color-mix(in oklch, var(--color-tomato) 36%, var(--color-line));
        color: var(--color-tomato);
      }

      .progress {
        display: grid;
        gap: 0.4rem;
        color: var(--color-muted);
        font-size: 0.8rem;
        font-weight: 720;
      }

      .mission-workbench {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: start;
        min-width: 0;
      }

      .mission-document {
        display: grid;
        min-width: 0;
        gap: 0.85rem;
      }

      .mission-document__paper {
        display: grid;
        min-width: 0;
        gap: 1rem;
        padding: clamp(0.7rem, 1.6vw, 1rem);
      }
    `,
  ],
})
export class AgentMissionEditorStepComponent {
  readonly store = inject(AgentBuilderStore);
  readonly isGenerating = computed(
    () => this.store.missionRunStatus() === 'running' || this.store.missionRunStatus() === 'queued',
  );
}
